/* Kids store: product page enhancements. Everything here is optional:
   without JS the page still shows a day range and the normal buy buttons. */

if (!customElements.get('kids-delivery-estimate')) {
  class KidsDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;

      const business = this.dataset.business === 'true';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const from = this.addDays(new Date(), min, business);
      const to = this.addDays(new Date(), Math.max(min, max), business);

      let format;
      try {
        format = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' });
      } catch (e) {
        format = new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' });
      }

      const strong = document.createElement('strong');
      strong.textContent =
        from.toDateString() === to.toDateString()
          ? format.format(from)
          : `${format.format(from)} – ${format.format(to)}`;
      this.replaceChildren(strong);
    }

    addDays(date, days, businessOnly) {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (!businessOnly || (day !== 0 && day !== 6)) added += 1;
      }
      return result;
    }
  }

  customElements.define('kids-delivery-estimate', KidsDeliveryEstimate);
}

if (!customElements.get('kids-sticky-atc')) {
  class KidsStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.target = this.productInfo?.querySelector('.product-form__buttons');
      if (!this.productInfo || !this.form || !this.target || !('IntersectionObserver' in window)) return;

      this.button = this.querySelector('.kids-sticky-atc__button');
      this.priceEl = this.querySelector('.kids-sticky-atc__price');
      this.variantEl = this.querySelector('.kids-sticky-atc__variant');

      this.hidden = false;
      this.scrolledPast = false;
      this.ctaVisible = false;
      this.cta = document.querySelector('kids-product-cta');
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.target) {
            this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          } else {
            this.ctaVisible = entry.isIntersecting;
          }
        });
        const show = this.scrolledPast && !this.ctaVisible;
        this.classList.toggle('is-visible', show);
        document.body.classList.toggle('kids-sticky-atc-active', show);
      });
      this.observer.observe(this.target);
      if (this.cta) this.observer.observe(this.cta);

      this.onChange = this.debounce(() => this.sync(), 50);
      this.mutationObserver = new MutationObserver(this.onChange);
      this.mutationObserver.observe(this.productInfo.querySelector('.product__info-container') || this.productInfo, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.mutationObserver?.disconnect();
      document.body.classList.remove('kids-sticky-atc-active');
    }

    sync() {
      const priceWrap = document.querySelector(`#price-${this.sectionId} .price`);
      if (priceWrap && this.priceEl) {
        const selector = priceWrap.classList.contains('price--on-sale')
          ? '.price__sale .price-item--last'
          : '.price__regular .price-item--regular';
        const current = priceWrap.querySelector(selector);
        if (current) this.priceEl.textContent = current.textContent.trim();
      }

      const mainButton = this.form.querySelector('[name="add"]');
      if (mainButton && this.button) {
        this.button.disabled = mainButton.disabled;
        const label = mainButton.querySelector('span')?.textContent.trim();
        if (label) this.button.textContent = label;
      }

      if (this.variantEl) {
        const variantData = this.productInfo.querySelector('variant-selects [data-selected-variant]');
        if (variantData) {
          try {
            const variant = JSON.parse(variantData.textContent);
            if (variant?.title) this.variantEl.textContent = variant.title;
          } catch (e) {
            /* keep the server-rendered title */
          }
        }
      }
    }

    debounce(fn, wait) {
      let timeout;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(fn, wait);
      };
    }
  }

  customElements.define('kids-sticky-atc', KidsStickyAtc);
}

/* Placeholder gallery (product has no photos yet): color thumbnails mirror the
   variant picker. Clicking a thumbnail selects that color; choosing a color
   re-tints the main placeholder. */
if (!customElements.get('kids-placeholder-gallery')) {
  class KidsPlaceholderGallery extends HTMLElement {
    connectedCallback() {
      this.optionName = this.dataset.optionName;
      this.sectionId = this.dataset.sectionId;
      this.main = this.querySelector('[data-ph-main]');
      if (!this.optionName) return;

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('.kids-ph__thumb[data-value]');
        if (!thumb) return;
        this.selectOption(thumb.dataset.value);
        this.setActive(thumb.dataset.value);
      });

      this.onChange = (event) => {
        const target = event.target;
        if (!target.closest(`#variant-selects-${this.sectionId}`)) return;
        if (target.type === 'radio' && target.dataset.optionName === this.optionName) {
          this.setActive(target.value);
        } else if (target.tagName === 'SELECT' && target.name === `options[${this.optionName}]`) {
          this.setActive(target.value);
        }
      };
      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener('change', this.onChange);
    }

    selectOption(value) {
      const picker = document.getElementById(`variant-selects-${this.sectionId}`);
      if (!picker) return;
      const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
        (input) => input.dataset.optionName === this.optionName && input.value === value
      );
      if (radio) {
        if (!radio.checked) radio.click();
        return;
      }
      const select = picker.querySelector(`select[name="options[${CSS.escape(this.optionName)}]"]`);
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    setActive(value) {
      let tint = '';
      let swatch = '';
      this.querySelectorAll('.kids-ph__thumb[data-value]').forEach((thumb) => {
        const active = thumb.dataset.value === value;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) {
          tint = thumb.style.getPropertyValue('--kids-ph-tint');
          swatch = thumb.style.getPropertyValue('--kids-ph-swatch');
        }
      });
      if (!this.main) return;
      ['--kids-ph-tint', '--kids-ph-swatch'].forEach((prop, index) => {
        const next = index === 0 ? tint : swatch;
        if (next) {
          this.main.style.setProperty(prop, next);
        } else {
          this.main.style.removeProperty(prop);
        }
      });
      const chipName = this.main.querySelector('[data-ph-chip-name]');
      if (chipName) chipName.textContent = value;
    }
  }

  customElements.define('kids-placeholder-gallery', KidsPlaceholderGallery);
}

/* Final CTA: mirrors the main product selection and submits the main form. */
if (!customElements.get('kids-product-cta')) {
  class KidsProductCta extends HTMLElement {
    connectedCallback() {
      this.productInfo =
        document.getElementById(`MainProduct-${this.dataset.mainSectionId}`) ||
        document.querySelector('product-info[id^="MainProduct-"]');
      if (!this.productInfo) return;

      this.mainId = this.productInfo.dataset.section;
      this.form = document.getElementById(`product-form-${this.mainId}`);
      this.button = this.querySelector('[data-cta-button]');
      if (this.button && this.form) this.button.setAttribute('form', this.form.getAttribute('id'));

      const change = this.querySelector('[data-cta-change]');
      if (change) {
        change.addEventListener('click', (event) => {
          const picker = document.getElementById(`variant-selects-${this.mainId}`);
          if (!picker) return;
          event.preventDefault();
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      let timeout;
      this.observer = new MutationObserver(() => {
        clearTimeout(timeout);
        timeout = setTimeout(() => this.sync(), 60);
      });
      this.observer.observe(this.productInfo.querySelector('.product__info-container') || this.productInfo, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'checked'],
      });
      this.onChange = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => this.sync(), 60);
      };
      document.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.onChange) document.removeEventListener('change', this.onChange);
    }

    sync() {
      const picker = document.getElementById(`variant-selects-${this.mainId}`);
      let variant = null;
      const data = picker?.querySelector('[data-selected-variant]');
      if (data) {
        try {
          variant = JSON.parse(data.textContent);
        } catch (e) {
          variant = null;
        }
      }

      const variantEl = this.querySelector('[data-cta-variant]');
      if (variant && variantEl && Array.isArray(variant.options)) {
        variantEl.textContent = variant.options.join(' · ');
      }

      const priceWrap = document.querySelector(`#price-${this.mainId} .price`);
      const priceEl = this.querySelector('[data-cta-price]');
      if (priceWrap && priceEl) {
        const selector = priceWrap.classList.contains('price--on-sale')
          ? '.price__sale .price-item--last'
          : '.price__regular .price-item--regular';
        const current = priceWrap.querySelector(selector);
        if (current) priceEl.textContent = current.textContent.trim();

        const onSale = priceWrap.classList.contains('price--on-sale');
        let compareEl = this.querySelector('[data-cta-compare]');
        const compareSource = priceWrap.querySelector('.price__sale s .price-item--regular, .price__sale .price-item--regular');
        if (onSale && compareSource) {
          if (!compareEl) {
            compareEl = document.createElement('s');
            compareEl.className = 'kids-cta__compare';
            compareEl.dataset.ctaCompare = '';
            priceEl.after(compareEl);
          }
          compareEl.textContent = compareSource.textContent.trim();
        } else if (compareEl) {
          compareEl.remove();
        }
      }

      const mainButton = this.form?.querySelector('[name="add"]');
      if (mainButton && this.button) {
        this.button.disabled = mainButton.disabled;
        const label = mainButton.disabled
          ? mainButton.querySelector('span')?.textContent.trim()
          : this.button.dataset.label;
        if (label) this.button.textContent = label;
      }

      const colorOption = this.dataset.colorOption;
      if (colorOption && picker) {
        let value = null;
        const radio = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).find(
          (input) => input.dataset.optionName === colorOption
        );
        if (radio) value = radio.value;
        const select = picker.querySelector(`select[name="options[${CSS.escape(colorOption)}]"]`);
        if (!value && select) value = select.value;

        if (value) {
          const thumb = document.querySelector(`.kids-ph__thumb[data-value="${CSS.escape(value)}"]`);
          const placeholder = this.querySelector('[data-cta-placeholder]');
          const dot = this.querySelector('[data-cta-dot]');
          const tint = thumb?.style.getPropertyValue('--kids-ph-tint');
          const swatch =
            thumb?.style.getPropertyValue('--kids-ph-swatch') ||
            radio?.nextElementSibling?.querySelector('.kids-swatch-dot')?.style.getPropertyValue('--kids-dot');
          if (placeholder && tint) placeholder.style.setProperty('--kids-ph-tint', tint);
          if (dot && swatch) dot.style.setProperty('--kids-dot', swatch);
        }
      }

      const image = this.querySelector('.kids-cta__image');
      const src = variant?.featured_image?.src;
      if (image && src) {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '600');
        image.removeAttribute('srcset');
        image.src = url.toString();
      }
    }
  }

  customElements.define('kids-product-cta', KidsProductCta);
}

/* Gallery thumbnails carry a color/print value (set in product-media-gallery.liquid)
   when their photo belongs to exactly one variant option value. Clicking one also
   selects that value in the variant picker, so the shown photo always matches what
   would be added to cart -- not just previewed. */
document.addEventListener('click', (event) => {
  const thumb = event.target.closest('[data-kids-color-value]');
  if (!thumb) return;

  const colorValue = thumb.getAttribute('data-kids-color-value');
  const optionName = thumb.getAttribute('data-kids-color-option');
  if (!colorValue || !optionName) return;

  const gallery = thumb.closest('media-gallery');
  if (!gallery || !gallery.id) return;

  const sectionId = gallery.id.replace('MediaGallery-', '');
  const variantSelects = document.getElementById(`variant-selects-${sectionId}`);
  if (!variantSelects) return;

  const radios = variantSelects.querySelectorAll('input[data-option-name]');
  for (const radio of radios) {
    if (radio.dataset.optionName !== optionName || radio.value !== colorValue) continue;
    if (radio.checked) break;
    const label = variantSelects.querySelector(`label[for="${radio.id}"]`);
    if (label) label.click();
    else radio.click();
    break;
  }
});
